import React, { useState, useEffect, useCallback, useRef } from 'react';
import './App.css';
import FaceAuth from './components/FaceAuth';
import BootSequence from './components/BootSequence';
import HolographicScene from './components/HolographicScene';
import ArcReactor from './components/ArcReactor';
import AudioVisualizer from './components/AudioVisualizer';
import TopAudioBar from './components/TopAudioBar';
import GestureController from './components/GestureController';
import ToolConfirmation from './components/ToolConfirmation';
import RepoPanel from './components/RepoPanel';
import { useWebSocket } from './hooks/useWebSocket';
import { GeminiVoiceClient } from './hooks/useGeminiVoice';
import { ClapDetector } from './utils/clapDetector';
import {
  playMicOn, playMicOff, playThinking, playResponseReady,
  playError, playStateChange, resumeAudio, playBase64Audio,
  startAmbient, stopAmbient, isAmbientRunning, stopCurrentAudio
} from './utils/audioEngine';
import { Mic, MicOff, Volume2, VolumeX, Zap, Brain, Moon, Sun, Trash2, Hand, Send, MessageSquare, X } from 'lucide-react';

function genId() { return 'jarvis_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now(); }

function App() {
  const [sessionId] = useState(() => {
    const s = localStorage.getItem('jarvis_session_id');
    if (s) return s;
    const n = genId();
    localStorage.setItem('jarvis_session_id', n);
    return n;
  });

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isBooted, setIsBooted] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [mode, setMode] = useState('quick');
  const [muted, setMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState('');
  const [messages, setMessages] = useState([]);
  const [currentResponse, setCurrentResponse] = useState('');
  const [currentTimings, setCurrentTimings] = useState(null);
  const [isBusy, setIsBusy] = useState(false);
  const [textInput, setTextInput] = useState('');
  const [displayedText, setDisplayedText] = useState('');
  const typewriterRef = useRef(null);
  const mutedRef = useRef(muted);
  const chatEndRef = useRef(null);

  // New state
  const [gesturesEnabled, setGesturesEnabled] = useState(false);
  const [audioIntensity, setAudioIntensity] = useState(0);
  const [micAudioData, setMicAudioData] = useState(new Array(32).fill(0));
  const [toolConfirmation, setToolConfirmation] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState('');
  const [transcript, setTranscript] = useState('');
  const [isVoiceLive, setIsVoiceLive] = useState(false);
  const [geminiConnected, setGeminiConnected] = useState(false);
  const [clapActive, setClapActive] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => { mutedRef.current = muted; }, [muted]);

  // Auto-scroll chat
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, displayedText]);

  // WS message handler
  const handleWsMessage = useCallback((msg) => {
    if (msg.type === 'thinking_update') {
      setDisplayedText(msg.phase === 'speaking' ? msg.text : msg.text);
      setCurrentResponse(msg.text);
      return;
    }
    if (msg.type === 'ack_audio') {
      // Skip ack audio to prevent voice overlap. Text ack is enough.
      return;
    }
    if (msg.type === 'response' || msg.type === 'command_response') {
      const text = msg.text || '';
      setCurrentResponse(text);
      setCurrentTimings(msg.timings || null);
      setDisplayedText(text);
      if (typewriterRef.current) clearInterval(typewriterRef.current);
      typewriterRef.current = null;
      setMessages(prev => {
        const last = prev[prev.length - 1];
        if (last && last.role === 'assistant' && last.text === text) return prev;
        return [...prev, { role: 'assistant', text, created_at: new Date().toISOString() }];
      });
      if (msg.audio_b64 && !mutedRef.current) {
        // Play ONLY ElevenLabs audio. No chirp sound overlapping.
        playBase64Audio(msg.audio_b64).catch(() => {});
      }
      setIsBusy(false);
      if (msg.state) {
        if (msg.state.mode) setMode(msg.state.mode);
        if (msg.state.muted !== undefined) setMuted(msg.state.muted);
      }
    }
    if (msg.type === 'state_change') {
      // No chirp sound — it overlaps with voice audio
    }
    if (msg.type === 'error') {
      setCurrentResponse(msg.message || 'Error');
      setDisplayedText(msg.message || 'Error');
      playError();
      setIsBusy(false);
    }
  }, []);

  const { isConnected, jarvisState, setJarvisState, sendVoiceInput, sendCommand, sendMessage } = useWebSocket(sessionId, handleWsMessage);

  // Gemini Voice — only connects when mic is clicked, NOT on boot
  const geminiRef = useRef(null);
  const geminiInitialized = useRef(false);

  const initGemini = useCallback(() => {
    if (geminiInitialized.current) return;
    geminiInitialized.current = true;
    
    const client = new GeminiVoiceClient({
      onTranscript: (t) => { setDisplayedText(p => p + t); setCurrentResponse(p => p + t); setJarvisState('SPEAKING'); },
      onUserTranscript: (t) => { setInterimTranscript(p => p + t); setJarvisState('LISTENING'); },
      onError: () => {},
      onStateChange: (s) => { setIsVoiceLive(s === 'listening'); },
      onConnected: (ok) => { setGeminiConnected(ok); }
    });
    geminiRef.current = client;
    client.connect(sessionId);
  }, [sessionId, setJarvisState]);

  // Clock
  useEffect(() => {
    const t = () => setCurrentTime(new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }));
    t(); const i = setInterval(t, 1000); return () => clearInterval(i);
  }, []);

  // SAFETY: Auto-reset isBusy after 20 seconds (prevents stuck state)
  const busyTimerRef = useRef(null);
  useEffect(() => {
    if (isBusy) {
      busyTimerRef.current = setTimeout(() => {
        console.warn('[SAFETY] Auto-resetting isBusy after timeout');
        setIsBusy(false);
        setJarvisState('ARMED');
      }, 20000);
    } else {
      if (busyTimerRef.current) clearTimeout(busyTimerRef.current);
    }
    return () => { if (busyTimerRef.current) clearTimeout(busyTimerRef.current); };
  }, [isBusy, setJarvisState]);

  // SAFETY: Reset isBusy when state returns to ARMED
  useEffect(() => {
    if (jarvisState === 'ARMED' && isBusy) {
      setIsBusy(false);
    }
  }, [jarvisState, isBusy]);

  // Load history
  useEffect(() => {
    if (!isConnected) return;
    const load = async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/history/${sessionId}`);
        if (r.ok) { const d = await r.json(); setMessages(d.messages || []); }
      } catch {}
    };
    load();
  }, [isConnected, sessionId]);

  // Boot complete
  const clapDetectorRef = useRef(null);
  const jarvisStateRef = useRef(jarvisState);
  const isListeningRef = useRef(isListening);
  const handleStartRef = useRef(null);
  useEffect(() => { jarvisStateRef.current = jarvisState; }, [jarvisState]);
  useEffect(() => { isListeningRef.current = isListening; }, [isListening]);

  const handleBootComplete = useCallback(() => {
    setIsBooted(true);
    // Welcome audio is already playing (crossfaded from BootSequence)
    // Ambient will start when welcome ends (handled in BootSequence)
    
    // Clap detection
    setTimeout(() => {
      const d = new ClapDetector(() => {
        if (jarvisStateRef.current === 'STANDBY') sendMessage({ type: 'command', command: 'wake up' });
        if (!isListeningRef.current) handleStartRef.current?.();
      });
      d.start().then(ok => { if (ok) setClapActive(true); });
      clapDetectorRef.current = d;
    }, 8000);
  }, [sendMessage]);

  useEffect(() => { return () => { if (clapDetectorRef.current) clapDetectorRef.current.stop(); }; }, []);

  const handleFirstInteraction = useCallback(() => { if (!hasInteracted) { setHasInteracted(true); resumeAudio(); } }, [hasInteracted]);

  // REST fallback
  const sendViaRest = useCallback(async (text, m, mu) => {
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/chat`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, session_id: sessionId, mode: m, muted: mu })
      });
      if (r.ok) {
        const d = await r.json();
        if (d.type === 'response') {
          setCurrentResponse(d.text || ''); setDisplayedText(d.text || '');
          setMessages(p => [...p, { role: 'assistant', text: d.text, created_at: new Date().toISOString() }]);
          if (d.audio_b64 && !mu) { playBase64Audio(d.audio_b64).catch(() => {}); }

          setJarvisState('ARMED'); setIsBusy(false);
        }
      }
    } catch { setIsBusy(false); setJarvisState('ARMED'); }
  }, [sessionId, setJarvisState]);

  // Voice handlers
  const handleStartListening = useCallback(() => {
    handleFirstInteraction();
    if (isBusy || jarvisState === 'STANDBY') return;
    setTranscript(''); setInterimTranscript(''); setDisplayedText(''); setCurrentResponse('');
    
    // Initialize Gemini on FIRST mic click (not on boot)
    if (!geminiRef.current) initGemini();
    
    // Small delay to let Gemini connect if first time
    const startMic = () => {
      if (geminiRef.current) {
        geminiRef.current.startMic().then(ok => { 
          if (ok) { playMicOn(); setIsListening(true); setJarvisState('LISTENING'); } 
        });
      }
    };
    
    if (geminiInitialized.current && geminiRef.current) {
      startMic();
    } else {
      // First time — wait for connection
      setTimeout(startMic, 1500);
    }
  }, [isBusy, jarvisState, handleFirstInteraction, setJarvisState, initGemini]);

  useEffect(() => { handleStartRef.current = handleStartListening; }, [handleStartListening]);

  const handleStopListening = useCallback(() => {
    playMicOff(); setIsListening(false);
    if (geminiRef.current) geminiRef.current.stopMic();
    if (interimTranscript) setMessages(p => [...p, { role: 'user', text: interimTranscript, created_at: new Date().toISOString() }]);
    if (currentResponse) setMessages(p => [...p, { role: 'assistant', text: currentResponse, created_at: new Date().toISOString() }]);
    setTranscript(interimTranscript); setInterimTranscript(''); setJarvisState('ARMED');
  }, [interimTranscript, currentResponse, setJarvisState]);

  // Text submit — with auto-retry via REST if WS response doesn't arrive
  const responseTimeoutRef = useRef(null);
  
  const handleTextSubmit = useCallback((e) => {
    e?.preventDefault(); handleFirstInteraction();
    if (!textInput.trim() || isBusy || jarvisState === 'STANDBY') return;
    const t = textInput.trim(); setTextInput(''); setIsBusy(true);
    setMessages(p => [...p, { role: 'user', text: t, created_at: new Date().toISOString() }]);
    setCurrentResponse(''); setDisplayedText(''); setCurrentTimings(null);
    setJarvisState('THINKING');
    
    const sent = sendVoiceInput(t, mode, muted);
    
    if (!sent) {
      // WS not connected — go straight to REST
      sendViaRest(t, mode, muted);
    } else {
      // WS sent — but set a timeout: if no response in 12s, retry via REST
      if (responseTimeoutRef.current) clearTimeout(responseTimeoutRef.current);
      responseTimeoutRef.current = setTimeout(() => {
        if (isBusy) {
          console.warn('[TIMEOUT] No WS response in 12s, falling back to REST');
          sendViaRest(t, mode, muted);
        }
      }, 12000);
    }
  }, [textInput, isBusy, jarvisState, mode, muted, sendVoiceInput, sendViaRest, handleFirstInteraction, setJarvisState]);
  
  // Clear timeout when response arrives
  useEffect(() => {
    if (!isBusy && responseTimeoutRef.current) {
      clearTimeout(responseTimeoutRef.current);
      responseTimeoutRef.current = null;
    }
  }, [isBusy]);

  // Keyboard
  useEffect(() => {
    const kd = (e) => {
      if (e.code === 'Space' && e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA') {
        e.preventDefault();
        if (isListening) handleStopListening();
        else if (!isBusy) handleStartListening();
      }
      if (e.code === 'Escape') { if (!isBooted) setIsBooted(true); else if (isListening) handleStopListening(); }
    };
    window.addEventListener('keydown', kd);
    return () => window.removeEventListener('keydown', kd);
  }, [isBooted, isListening, isBusy, handleStartListening, handleStopListening]);

  // Commands
  const handleToggleMute = useCallback(() => { setMuted(m => !m); sendCommand(muted ? 'unmute' : 'mute'); }, [muted, sendCommand]);
  const handleClearHistory = useCallback(async () => {
    try { await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/history/${sessionId}`, { method: 'DELETE' }); } catch {}
    setMessages([]); setCurrentResponse(''); setDisplayedText('');
  }, [sessionId]);

  // Status text
  const statusText = jarvisState === 'LISTENING' ? 'LISTENING...'
    : jarvisState === 'THINKING' ? 'PROCESSING...'
    : jarvisState === 'SPEAKING' ? 'SPEAKING...'
    : jarvisState === 'STANDBY' ? 'STANDBY'
    : 'READY';

  const statusColor = jarvisState === 'LISTENING' ? '#19F2D6'
    : jarvisState === 'THINKING' ? '#FFB020'
    : jarvisState === 'SPEAKING' ? '#00D7FF'
    : jarvisState === 'STANDBY' ? '#7C8AA6'
    : '#00D7FF';

  // === RENDER FLOW: FaceAuth → Boot → HUD ===
  if (!isAuthenticated) {
    return <FaceAuth onAuthenticated={() => setIsAuthenticated(true)} />;
  }

  if (!isBooted) return <BootSequence onComplete={handleBootComplete} />;

  return (
    <div style={{ width: '100vw', height: '100vh', position: 'relative' }} onClick={handleFirstInteraction}>
      {/* === BACKGROUND LAYERS === */}
      <div className="bg-gradient" />
      <div className="bg-noise" />
      <div className="bg-glow" />
      <div className="scan-line" />
      <HolographicScene state={jarvisState} />

      {/* === STANDBY OVERLAY === */}
      {jarvisState === 'STANDBY' && (
        <div className="standby-overlay">
          <div className="label">STANDBY MODE</div>
          <div className="sub">DOUBLE CLAP OR SAY "WAKE UP" TO REACTIVATE</div>
        </div>
      )}

      {/* === TOP BAR === */}
      <div className="top-bar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <span className="title">J.A.R.V.I.S</span>
          <span className={`status-pill ${jarvisState?.toLowerCase()}`}>{jarvisState}</span>
          {clapActive && <span className="info" style={{ color: '#19F2D6' }}>CLAP ARMED</span>}
        </div>
        <TopAudioBar audioData={micAudioData} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          {geminiConnected && <span className="info green">GEMINI</span>}
          <span className="info">{mode === 'deep' ? 'DEEP' : 'QUICK'}</span>
          <span className="info">{currentTime}</span>
        </div>
      </div>

      {/* === CENTRAL VISUALIZER === */}
      <div className="visualizer-container">
        <div style={{ position: 'relative' }}>
          <ArcReactor state={jarvisState} />
          <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', pointerEvents: 'none' }}>
            <AudioVisualizer isActive={jarvisState === 'SPEAKING' || jarvisState === 'LISTENING'} intensity={audioIntensity} state={jarvisState} />
          </div>
        </div>
        <button
          className={`mic-btn ${isListening ? 'listening' : ''} ${isBusy || jarvisState === 'STANDBY' ? 'disabled' : ''}`}
          onClick={() => { if (isListening) handleStopListening(); else handleStartListening(); }}
          style={{ marginTop: 16, pointerEvents: 'auto' }}
          data-testid="push-to-talk-button"
        >
          {isListening ? <MicOff size={24} /> : <Mic size={24} />}
        </button>
        <div className="visualizer-status" style={{ color: statusColor }}>{statusText}</div>
      </div>

      {/* === LEFT PANEL === */}
      <div className="glass-panel" style={{
        position: 'fixed', top: 56, left: 16, width: 230, zIndex: 30,
        padding: '14px 16px', fontSize: 10,
        maxHeight: 'calc(100vh - 130px)', overflowY: 'auto',
      }}>
        <div className="section-title">◈ OPERATOR</div>
        <div style={{ color: '#fff', fontSize: 13, fontWeight: 700, letterSpacing: '0.05em' }}>HASAN</div>
        <div style={{ color: '#19F2D6', fontSize: 9, letterSpacing: '0.12em', marginBottom: 2 }}>Genesis Prime — CEO & Founder</div>
        <div style={{ color: '#7C8AA6', fontSize: 9, marginBottom: 10 }}>Nexis365 Command Center</div>

        <div style={{ borderTop: '1px solid rgba(0,215,255,0.08)', paddingTop: 10, marginBottom: 6 }}>
          <div style={{ color: '#00D7FF', fontWeight: 700, letterSpacing: '0.2em', marginBottom: 8, fontSize: 9 }}>◈ SYSTEM</div>
        </div>
        {[
          ['WS', isConnected ? 'ONLINE' : 'OFFLINE', isConnected ? '#39FFB6' : '#FF3B3B'],
          ['LLM', mode === 'deep' ? 'SONNET 4' : 'HAIKU', '#B9F3FF'],
          ['TTS', muted ? 'MUTED' : 'DANIEL', muted ? '#FFB020' : '#39FFB6'],
          ['VOICE', geminiConnected ? 'GEMINI LIVE' : 'STANDBY', geminiConnected ? '#39FFB6' : '#7C8AA6'],
          ['MODE', mode.toUpperCase(), '#00D7FF'],
        ].map(([k, v, c]) => (
          <div key={k} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
            <span style={{ color: '#555' }}>{k}</span>
            <span style={{ color: c, fontWeight: 600 }}>{v}</span>
          </div>
        ))}

        <div style={{ borderTop: '1px solid rgba(0,215,255,0.08)', paddingTop: 10, marginTop: 6 }}>
          <div style={{ color: '#00D7FF', fontWeight: 700, letterSpacing: '0.2em', marginBottom: 8, fontSize: 9 }}>◈ TEAM</div>
        </div>
        <div style={{ color: '#B9F3FF', marginBottom: 3 }}>Adil A. <span style={{ color: '#555' }}>— CTO</span></div>
        <div style={{ color: '#B9F3FF', marginBottom: 3 }}>Farhan R. <span style={{ color: '#555' }}>— CISO</span></div>
      </div>

      {/* === RIGHT PANEL: LIVE REPO ACTIVITY === */}
      <RepoPanel />

      {/* === CHAT MODULE === */}
      <div className="chat-module glass-panel">
        <div className="chat-noise" />
        <div className="chat-messages" style={{ position: 'relative', zIndex: 2 }}>
          {messages.slice(-8).map((m, i) => (
            <div key={i} className="chat-msg">
              <div className={`sender ${m.role}`}>{m.role === 'user' ? 'BOSS' : 'JARVIS'}</div>
              <div className="text">{m.text}</div>
            </div>
          ))}
          {(isBusy || jarvisState === 'SPEAKING') && displayedText && (
            <div className="chat-msg">
              <div className="sender jarvis">JARVIS</div>
              <div className={`text ${isBusy ? 'thinking' : ''}`}>{displayedText}</div>
            </div>
          )}
          {isListening && interimTranscript && (
            <div className="chat-msg">
              <div className="sender user">BOSS</div>
              <div className="text" style={{ color: 'rgba(255,179,0,0.6)' }}>{interimTranscript}</div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>
        <form className="chat-input-row" onSubmit={handleTextSubmit} style={{ position: 'relative', zIndex: 2 }}>
          <input
            type="text" value={textInput} onChange={e => setTextInput(e.target.value)}
            placeholder={jarvisState === 'STANDBY' ? 'Type "wake up"...' : 'Speak to Jarvis...'}
            disabled={isBusy} data-testid="text-input"
          />
          <button type="submit" disabled={!textInput.trim() || isBusy} data-testid="text-send-button">
            <Send size={14} />
          </button>
        </form>
      </div>

      {/* === FLOATING TOOLBAR (ADA pill-shape) === */}
      <div className="toolbar">
        <button className={`tool-btn ${!muted ? 'active' : 'active-red'}`} onClick={handleToggleMute} title={muted ? 'Unmute' : 'Mute'}>
          {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
        </button>
        <button className={`tool-btn ${mode === 'quick' ? 'active' : ''}`} onClick={() => { setMode('quick'); sendCommand('quick_mode'); }} title="Quick">
          <Zap size={18} />
        </button>
        <button className={`tool-btn ${mode === 'deep' ? 'active' : ''}`} onClick={() => { setMode('deep'); sendCommand('deep_mode'); }} title="Deep">
          <Brain size={18} />
        </button>
        <button className={`tool-btn ${gesturesEnabled ? 'active-amber' : ''}`} onClick={() => setGesturesEnabled(!gesturesEnabled)} title="Gestures">
          <Hand size={18} />
        </button>
        <button
          className={`tool-btn ${jarvisState === 'STANDBY' ? 'active-green' : ''}`}
          onClick={() => sendMessage({ type: 'command', command: jarvisState === 'STANDBY' ? 'wake up' : 'stand down' })}
          title={jarvisState === 'STANDBY' ? 'Wake' : 'Standby'}
        >
          {jarvisState === 'STANDBY' ? <Sun size={18} /> : <Moon size={18} />}
        </button>
        <button className="tool-btn" onClick={() => setShowHistory(!showHistory)} title="History">
          <MessageSquare size={18} />
        </button>
        <button className="tool-btn" onClick={handleClearHistory} title="Clear">
          <Trash2 size={18} />
        </button>
      </div>

      {/* === HISTORY DRAWER === */}
      {showHistory && (
        <div style={{
          position: 'fixed', top: 50, right: 16, bottom: 80, width: 320, zIndex: 60,
          background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
          border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
        }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontFamily: 'JetBrains Mono', fontSize: 10, letterSpacing: '0.2em', color: '#22d3ee' }}>HISTORY</span>
            <button onClick={() => setShowHistory(false)} style={{ background: 'none', border: 'none', color: '#7C8AA6', cursor: 'pointer' }}><X size={14} /></button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '8px 12px' }}>
            {messages.map((m, i) => (
              <div key={i} style={{ padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.03)', fontFamily: 'JetBrains Mono', fontSize: 11 }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: m.role === 'user' ? '#FFB300' : '#22d3ee', letterSpacing: '0.1em' }}>
                  {m.role === 'user' ? 'BOSS' : 'JARVIS'}
                </div>
                <div style={{ color: 'rgba(220,235,255,0.6)', marginTop: 2, lineHeight: 1.5 }}>{m.text?.substring(0, 120)}{m.text?.length > 120 ? '...' : ''}</div>
              </div>
            ))}
            {!messages.length && <div style={{ color: 'rgba(124,138,166,0.4)', textAlign: 'center', padding: 40, fontSize: 11, fontFamily: 'JetBrains Mono' }}>No conversations yet.</div>}
          </div>
        </div>
      )}

      {/* === GESTURE CONTROLLER === */}
      <GestureController enabled={gesturesEnabled} onToggle={() => setGesturesEnabled(!gesturesEnabled)} />

      {/* === TOOL CONFIRMATION === */}
      <ToolConfirmation request={toolConfirmation} onConfirm={() => setToolConfirmation(null)} onDeny={() => setToolConfirmation(null)} />
    </div>
  );
}

export default App;
